import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Bird, Bone, EllipsisVertical, Fish, FolderOpen, ImageUp, PawPrint, Search, Shapes, Sparkles, Trash2, Users, Wand2, X, type LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  catalogPuppetsInfiniteQuery,
  catalogStatus,
  puppetsQuery,
  type CatalogPuppet,
  type PuppetKind,
  type PuppetProjectSummary,
  type PuppetStatus,
} from '@/api/puppets'
import { deleteProject, importSkf, projectsQuery, skelKeys, thumbUrl } from '@/api/skel'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'

import { makeAllMove, makeItMove, resumeRunningBatch, useAutorigStore } from './autorig'
import { PuppetCard } from './PuppetCard'

/**
 * `/puppets` — every catalog character, animal and prop as a puppet-to-be, plus the parent's own
 * (parts sheet, `.skf` import, blank). One click "Make it move" rigs an asset (a background job
 * with live progress); "Make all move" rigs the lot. A ready puppet opens in the editor
 * (`/skel/$id`) or goes straight into a story (`/stories/new?cast=skel:<id>`).
 */

export const PUPPET_TABS = ['people', 'animals', 'birds', 'sea', 'props', 'mine'] as const
export type PuppetTab = (typeof PUPPET_TABS)[number]

const TAB_INFO: Record<PuppetTab, { label: string; icon: LucideIcon; kinds: PuppetKind[] }> = {
  people: { label: 'People', icon: Users, kinds: ['person'] },
  animals: { label: 'Animals', icon: PawPrint, kinds: ['animal', 'creature'] },
  birds: { label: 'Birds', icon: Bird, kinds: ['bird'] },
  sea: { label: 'Sea', icon: Fish, kinds: ['fish'] },
  props: { label: 'Props', icon: Shapes, kinds: ['object'] },
  mine: { label: 'My puppets', icon: Wand2, kinds: [] },
}

export interface PuppetsPageProps {
  tab?: PuppetTab
  q?: string
  onSearchChange?(next: { tab?: PuppetTab; q?: string }): void
}

/** "land_animals" → "Land animals". */
export function prettyCategory(s: string | null | undefined): string | null {
  if (!s) return null
  const t = s.replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : null
}

export function PuppetsPage({ tab = 'people', q = '', onSearchChange }: PuppetsPageProps) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const importInput = useRef<HTMLInputElement | null>(null)
  const [draft, setDraft] = useState(q)
  // The box is the source while typing; the URL follows 250 ms later (and wins when it changes).
  useEffect(() => setDraft(q), [q])
  useEffect(() => {
    if (draft === q) return
    // Untrimmed: trimming here would eat the space someone is about to type ("red |fox").
    const t = setTimeout(() => onSearchChange?.({ tab, q: draft || undefined }), 250)
    return () => clearTimeout(t)
  }, [draft, q, tab, onSearchChange])

  useEffect(() => void resumeRunningBatch(qc), [qc])

  const importer = useMutation({
    mutationFn: (file: File) => importSkf(file),
    onSuccess: (env) => {
      qc.setQueryData(skelKeys.project(env.id), env)
      void qc.invalidateQueries({ queryKey: skelKeys.projects() })
      toast.success('Imported', { description: `“${env.body.name}” is ready to open.` })
      void navigate({ to: '/skel/$id', params: { id: env.id } })
    },
    onError: (err) => toast.error('Could not import that file', { description: err instanceof Error && err.message ? err.message : 'Is it a SkelForm .skf file?' }),
  })

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8" data-testid="puppets-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex max-w-2xl flex-col gap-1">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">Puppets</h1>
          <p className="text-ink-2">Any character, animal or prop can become a puppet that walks, waves and talks in your stories.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="primary">
            <Link to="/skel/split" search={{}} data-testid="from-one-picture">
              <Bone aria-hidden />
              From one picture
            </Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/skel/new">
              <ImageUp aria-hidden />
              From a parts sheet
            </Link>
          </Button>
          <Button variant="secondary" onClick={() => importInput.current?.click()} loading={importer.isPending}>
            {importer.isPending ? null : <FolderOpen aria-hidden />}
            Import .skf
          </Button>
          <input
            ref={importInput}
            type="file"
            accept=".skf,application/zip"
            className="hidden"
            data-testid="import-skf-input"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) importer.mutate(file)
            }}
          />
        </div>
      </header>

      <BatchBanner />

      <Tabs value={tab} onValueChange={(v) => onSearchChange?.({ tab: v as PuppetTab, q: q || undefined })}>
        <div className="flex flex-wrap items-center gap-3">
          <TabsList className="h-auto flex-wrap" aria-label="Kinds of puppets">
            {PUPPET_TABS.map((t) => {
              const Icon = TAB_INFO[t].icon
              return (
                <TabsTrigger key={t} value={t} className="h-10 px-3.5 text-sm" data-testid={`tab-${t}`}>
                  <Icon className="size-4" aria-hidden />
                  {TAB_INFO[t].label}
                </TabsTrigger>
              )
            })}
          </TabsList>
          <div className="relative min-w-56 flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Find a puppet: fox, doctor, boat…" aria-label="Search puppets" className="pl-9 pr-9" />
            {draft ? (
              <button type="button" onClick={() => setDraft('')} aria-label="Clear search" className="absolute right-2 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-ink-3 hover:bg-paper-2 hover:text-ink">
                <X className="size-4" aria-hidden />
              </button>
            ) : null}
          </div>
          {tab !== 'mine' ? <MakeAllMoveButton /> : null}
        </div>

        {PUPPET_TABS.map((t) => (
          <TabsContent key={t} value={t} className="pt-4">
            {t === 'mine' ? <MyPuppets q={q} /> : <CatalogGrid kinds={TAB_INFO[t].kinds} q={q} tabLabel={TAB_INFO[t].label} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Catalog grid                                                                */
/* -------------------------------------------------------------------------- */

const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(11.5rem,1fr))] gap-4'

function CatalogGrid({ kinds, q, tabLabel }: { kinds: PuppetKind[]; q: string; tabLabel: string }) {
  const qc = useQueryClient()
  const live = useAutorigStore((s) => s.byUid)
  const feed = useQuery(puppetsQuery({}))
  const readyById = useMemo(() => {
    const m = new Map<string, boolean>()
    for (const p of feed.data?.items ?? []) m.set(p.project_id, p.story_ready)
    return m
  }, [feed.data])

  const list = useInfiniteQuery({
    ...catalogPuppetsInfiniteQuery(kinds, q),
    // Rigs started elsewhere (the batch, another tab) flip their badges without a reload.
    refetchInterval: (query) => (query.state.data?.pages.some((p) => p.items.some((i) => i.link?.status === 'queued' || i.link?.status === 'rigging')) ? 4_000 : false),
  })
  const items = useMemo(() => dedupe(list.data?.pages.flatMap((p) => p.items) ?? []), [list.data])

  const statusOf = (item: CatalogPuppet): PuppetStatus => {
    const l = live[item.asset_uid]
    const pid = item.link?.project_id ?? l?.projectId ?? null
    return catalogStatus(item, { live: l?.status ?? null, storyReady: pid ? readyById.get(pid) : undefined })
  }

  if (list.isPending) return <GridSkeleton />
  if (list.isError) {
    return (
      <div className="card flex flex-col items-start gap-3 p-6" role="alert">
        <p className="text-ink">The puppets could not be loaded.</p>
        <Button variant="secondary" onClick={() => void list.refetch()}>
          Try again
        </Button>
      </div>
    )
  }
  if (items.length === 0) {
    return <p className="card p-8 text-center text-ink-2">{q ? `No ${tabLabel.toLowerCase()} match “${q}”.` : `No ${tabLabel.toLowerCase()} yet.`}</p>
  }

  return (
    <>
      <ul className={GRID} role="list" aria-label={tabLabel} data-testid="puppet-grid">
        {items.map((item) => {
          const l = live[item.asset_uid]
          const status = statusOf(item)
          return (
            <PuppetCard
              key={item.asset_uid}
              name={item.name}
              sub={prettyCategory(item.category) ?? prettyCategory(item.group)}
              thumb={item.thumb_url}
              status={status}
              progress={l && l.status === 'rigging' ? { msg: l.msg, pct: l.pct } : null}
              error={l?.error ?? item.link?.error ?? null}
              projectId={item.link?.project_id ?? l?.projectId ?? null}
              assetUid={item.asset_uid}
              onMakeItMove={() => void makeItMove(qc, item.asset_uid, { kind: item.kind, name: item.name, force: status === 'failed' })}
            />
          )
        })}
      </ul>
      <LoadMore hasMore={list.hasNextPage} loading={list.isFetchingNextPage} onMore={() => void list.fetchNextPage()} />
    </>
  )
}

function dedupe(items: CatalogPuppet[]): CatalogPuppet[] {
  const seen = new Set<string>()
  return items.filter((i) => (seen.has(i.asset_uid) ? false : (seen.add(i.asset_uid), true)))
}

/** Infinite scroll: a sentinel that asks for the next page as it nears the viewport, plus a button. */
function LoadMore({ hasMore, loading, onMore }: { hasMore: boolean; loading: boolean; onMore(): void }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const more = useRef(onMore)
  more.current = onMore
  useEffect(() => {
    const el = ref.current
    if (!el || !hasMore || loading || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && more.current(), { rootMargin: '600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loading])
  if (!hasMore) return null
  return (
    <div ref={ref} className="flex justify-center py-6">
      <Button variant="secondary" onClick={onMore} loading={loading}>
        Show more
      </Button>
    </div>
  )
}

function GridSkeleton() {
  return (
    <ul className={GRID} aria-busy="true" aria-label="Loading puppets">
      {Array.from({ length: 12 }, (_, i) => (
        <li key={i} className="card overflow-hidden">
          <div className="aspect-square animate-pulse-soft bg-paper-2" />
          <div className="flex flex-col gap-2 p-3">
            <div className="h-4 w-2/3 rounded bg-paper-2" />
            <div className="h-11 rounded-xl bg-paper-2" />
          </div>
        </li>
      ))}
    </ul>
  )
}

/* -------------------------------------------------------------------------- */
/* Make all move                                                               */
/* -------------------------------------------------------------------------- */

function MakeAllMoveButton() {
  const qc = useQueryClient()
  const running = useAutorigStore((s) => s.batch?.status === 'running')
  const [asking, setAsking] = useState(false)
  return (
    <>
      <Button variant="secondary" className="ml-auto" onClick={() => setAsking(true)} disabled={running} data-testid="make-all-move">
        <Sparkles aria-hidden />
        Make all move
      </Button>
      <Dialog open={asking} onOpenChange={setAsking}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Make every puppet move?</DialogTitle>
            <DialogDescription>
              Every character, animal and prop that cannot move yet learns how, one after another. It runs in the background — you can keep making stories. Puppets you changed by hand are left alone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAsking(false)}>
              Not now
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setAsking(false)
                void makeAllMove(qc)
              }}
            >
              Start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function BatchBanner() {
  const batch = useAutorigStore((s) => s.batch)
  const setBatch = useAutorigStore((s) => s.setBatch)
  if (!batch) return null
  const pct = batch.pct != null ? Math.round(Math.min(1, Math.max(0, batch.pct)) * 100) : null
  const tone = batch.status === 'failed' ? 'border-danger/40 bg-danger-soft' : batch.status === 'done' ? 'border-ok/40 bg-ok-soft' : 'border-info/30 bg-info-soft'
  return (
    <section className={cn('flex flex-col gap-2 rounded-2xl border p-4', tone)} role="status" aria-live="polite" data-testid="batch-banner">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-ink">
          {batch.status === 'running' ? 'Teaching every puppet to move…' : batch.status === 'done' ? 'Every puppet can move now.' : 'Making every puppet move stopped.'}
        </p>
        {batch.status !== 'running' ? (
          <Button variant="ghost" size="sm" onClick={() => setBatch(null)}>
            Dismiss
          </Button>
        ) : null}
      </div>
      {batch.status === 'running' ? (
        <>
          <div className="h-2 overflow-hidden rounded-full bg-paper" aria-hidden>
            <div className={cn('h-full rounded-full bg-info transition-[width]', pct == null && 'w-1/4 animate-pulse-soft')} style={pct != null ? { width: `${Math.max(3, pct)}%` } : undefined} />
          </div>
          <p className="text-sm text-ink-2">{batch.msg ?? 'Working…'}</p>
        </>
      ) : batch.error ? (
        <p className="text-sm text-danger">{batch.error}</p>
      ) : null}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* My puppets                                                                  */
/* -------------------------------------------------------------------------- */

/** The thumbnail URL with the project rev (and the file's own stamp) as cache-busters. */
function projectThumb(p: PuppetProjectSummary): string | null {
  if (!p.thumb_url) return null
  const stamp = /[?&]v=([^&#]+)/.exec(p.thumb_url)?.[1]
  const url = thumbUrl(p.id, p.rev)
  return stamp ? `${url}.${encodeURIComponent(stamp)}` : url
}

function MyPuppets({ q }: { q: string }) {
  const qc = useQueryClient()
  const projects = useQuery(projectsQuery())
  const [toDelete, setToDelete] = useState<PuppetProjectSummary | null>(null)
  const [askedName, setAskedName] = useState('')
  const nameOf = (p: PuppetProjectSummary): string => p.display_name || p.name || 'Untitled puppet'
  if (toDelete && nameOf(toDelete) !== askedName) setAskedName(nameOf(toDelete))

  const remove = useMutation({
    mutationFn: (p: PuppetProjectSummary) => deleteProject(p.id),
    onSuccess: (_v, p) => {
      qc.removeQueries({ queryKey: skelKeys.project(p.id) })
      void qc.invalidateQueries({ queryKey: skelKeys.projects() })
      void qc.invalidateQueries({ queryKey: ['skel', 'puppets'] })
      setToDelete(null)
      toast.success(`Deleted “${nameOf(p)}”`)
    },
    onError: (err) => toast.error('Could not delete it', { description: err instanceof Error ? err.message : 'Please try again.' }),
  })

  const all = (projects.data?.items ?? []) as PuppetProjectSummary[]
  const needle = q.trim().toLowerCase()
  const mine = all.filter((p) => !p.asset_uid).filter((p) => !needle || nameOf(p).toLowerCase().includes(needle))

  if (projects.isPending) return <GridSkeleton />
  if (projects.isError) return <p className="card p-6 text-ink-2" role="alert">Your puppets could not be loaded.</p>
  if (mine.length === 0) {
    return (
      <div className="card flex flex-col items-center gap-4 p-8 text-center" data-testid="mine-empty">
        <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent">
          <ImageUp className="size-7" aria-hidden />
        </span>
        <div className="flex max-w-md flex-col gap-1">
          <h2 className="text-xl font-semibold text-ink">{needle ? `Nothing matches “${q}”` : 'Make your own puppet'}</h2>
          <p className="text-sm text-ink-2">Draw a character as separate parts — head, body, arms, legs — take a photo, and the studio puts it together so it can move.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Button asChild variant="primary" size="lg">
            <Link to="/skel/split" search={{}}>From one picture</Link>
          </Button>
          <Button asChild variant="secondary" size="lg">
            <Link to="/skel/new">From a parts sheet</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <ul className={GRID} role="list" aria-label="My puppets" data-testid="mine-grid">
        {mine.map((p) => {
          const status: PuppetStatus = p.story_ready === false ? 'needs_moves' : 'ready'
          return (
            <PuppetCard
              key={p.id}
              testId="my-puppet-card"
              name={nameOf(p)}
              sub={p.tier === 'sheet' ? 'From a parts sheet' : `${p.anim_count} ${p.anim_count === 1 ? 'move' : 'moves'}`}
              thumb={projectThumb(p)}
              status={status}
              projectId={p.id}
              corner={
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-9 bg-paper/80 backdrop-blur" aria-label={`More for ${nameOf(p)}`}>
                      <EllipsisVertical aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem destructive onSelect={() => setToDelete(p)}>
                      <Trash2 className="size-4" aria-hidden />
                      Delete…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              }
            />
          )
        })}
      </ul>
      <Dialog open={toDelete !== null} onOpenChange={(o) => (!o ? setToDelete(null) : undefined)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete “{askedName}”?</DialogTitle>
            <DialogDescription>The puppet and all its moves go away. Stories already made with it still play.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => toDelete && remove.mutate(toDelete)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
